import { useNotifyUserConsentStorage } from '../../../../../AppClients';
import { validateCompletion } from '../../../../../backend/config/classifiers/AccountSectionClassifier/shared';
import { resetUserPasswordDef, unlockAccountDef } from '../../../../../backend/features/accounts/shared';
import { requireAnySystemPermission } from '../../../../../backend/features/permissions/shared';
import { useContactManagementTranslation, useUserState } from '../../../../../ui/hooks';
import { ContactManagementConfigContext } from '../../../../../ui/providers';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { ModalSlideoutActions, ModalSlideoutContent, ModalSlideoutHeader, ModalSlideoutTitleArea, ModalSlideoutTopBar, ModalSlideoutTopBarDefaultActions, } from '@juvare/common-web-ui/ModalSlideout';
import { PageError } from '@juvare/common-web-ui/PageError';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, Box, Button, Divider, List, ListItem, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useAccountSections } from '../../hooks/useAccountSections';
import { AccountCompletionBar } from '../AccountCompletionBar';
import { AccountProfilePicture } from '../AccountProfilePicture';
import { AccountStatusChip } from '../AccountStatusChip';
import { ContactInformation } from './components/ContactInformation';
import { oktaStatusType } from './types';
export const AccountDetails = (props) => {
    var _a;
    const { accountConfig } = React.useContext(ContactManagementConfigContext);
    const userState = useUserState();
    const settings = useSelector(getSettings);
    const { t } = useContactManagementTranslation();
    const notifyUserConsentClient = useNotifyUserConsentStorage();
    const sections = useAccountSections('details');
    const [phoneConsents, setPhoneConsents] = React.useState([]);
    const displayName = `${props.account.columns.lastName} ${props.account.columns.firstName}`;
    const showOktaStatusAlert = props.oktaStatus === oktaStatusType.LOCKED_OUT ||
        props.oktaStatus === oktaStatusType.PASSWORD_EXPIRED ||
        props.oktaStatus === oktaStatusType.PASSWORD_RESET ||
        props.oktaStatus === oktaStatusType.PENDING_USER_ACTION ||
        props.oktaStatus === oktaStatusType.RECOVERY;
    // TODO - CNEO-5539 top slideout component should handle loading data with useStorageList hook
    React.useEffect(() => {
        var _a;
        if (props.account && props.accountPermissions && props.accountPermissions.allowUpdateFull) {
            const phoneNumbers = (_a = props.account.data.contactPhoneNumbers) === null || _a === void 0 ? void 0 : _a.map((contactPhoneNumber) => contactPhoneNumber.phoneNumber);
            if (!!(phoneNumbers === null || phoneNumbers === void 0 ? void 0 : phoneNumbers.length)) {
                notifyUserConsentClient
                    .list({ filter: [{ column: 'phoneNumber', op: 'in', value: phoneNumbers }] })
                    .then((response) => setPhoneConsents(response.rows))
                    .catch(handleErrorSnackbar);
            }
        }
    }, [props.account, notifyUserConsentClient, settings, userState, props.accountPermissions]);
    const validationResult = React.useMemo(() => { var _a; return (props.account ? validateCompletion((_a = sections.data) !== null && _a !== void 0 ? _a : [], accountConfig, props.account.data) : undefined); }, [accountConfig, props.account, sections]);
    if (!validationResult || !props.accountPermissions) {
        return null;
    }
    const itemsNeedingAttention = validationResult.totalFieldCount - validationResult.completedFieldCount;
    const allowUpdateFull = props.accountPermissions.allowUpdateFull ||
        requireAnySystemPermission(userState, 'manage-accounts') ||
        (userState === null || userState === void 0 ? void 0 : userState.id) === props.account.id;
    const allowedToSeeOktaActions = requireAnySystemPermission(userState, 'allow-reset-okta-password-email', 'allow-reset-okta-mfa', 'allow-unlock-okta-account', 'allow-change-login-email');
    return (React.createElement(React.Fragment, null,
        React.createElement(ModalSlideoutTopBar, { isOpaque: !props.isScrolledToTop, gradient: 'purple-dark-angle' },
            React.createElement(ModalSlideoutTopBarDefaultActions, { menuItems: props.menuItems, onClose: () => props.onClose(false) })),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', flexGrow: 1 } },
            React.createElement(ModalSlideoutHeader, { gradient: 'purple-dark-angle', "data-test": 'header' },
                React.createElement(ModalSlideoutTitleArea, { titleTypographyVariant: 'h1', title: displayName, subTitle: props.subTitle, sx: { alignItems: 'center', display: 'flex', gap: 1 } },
                    React.createElement(AccountStatusChip, { status: props.account.data.systemStatus, label: t(`panels.accounts.status.${props.account.data.systemStatus}`) })),
                React.createElement(Box, { sx: { alignItems: 'center', display: 'flex' } },
                    React.createElement(AccountProfilePicture, { accountId: props.account.id, firstName: props.account.columns.firstName, lastName: props.account.columns.lastName, orgMembership: props.account.columns.orgMembership, hideUpload: true }),
                    React.createElement(Box, { sx: {
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            justifyContent: 'center',
                            ml: 2,
                        } }, allowUpdateFull && (React.createElement(AccountCompletionBar, { title: (_a = props.completionBarTitle) !== null && _a !== void 0 ? _a : t('panels.accounts.completion'), account: props.account })))),
                props.additionalHeaderContent),
            React.createElement(ModalSlideoutContent, { ariaDescribeAllChildren: false },
                settings.identityProvider.oktaActionsEnabled && allowedToSeeOktaActions && showOktaStatusAlert && (React.createElement(Box, { sx: { pt: 2, px: 2 } },
                    React.createElement(OktaStatusAlert, { accountId: props.account.id, oktaStatus: props.oktaStatus, reloadOktaStatus: props.reloadOktaStatus }))),
                React.createElement(ContactInformation, { accountId: props.account.id, account: props.account.data, phoneConsents: phoneConsents }),
                props.additionalContent,
                allowUpdateFull && itemsNeedingAttention > 0 && (React.createElement(React.Fragment, null,
                    React.createElement(Divider, null),
                    React.createElement(Box, { sx: { p: 2 } },
                        React.createElement(Typography, { sx: { color: (theme) => theme.palette.error.main, display: 'flex', columnGap: 1 }, variant: 'subtitle2' },
                            React.createElement(ErrorOutlineIcon, { fontSize: 'small' }),
                            " ",
                            t('panels.accounts.someInfoIsMissingFrom') + '...'),
                        React.createElement(List, null, validationResult.incompleteSections.map((incompleteSection) => {
                            var _a;
                            const sectionDetails = (_a = sections.data) === null || _a === void 0 ? void 0 : _a.find((section) => section.id === incompleteSection.id);
                            return (React.createElement(ListItem, { key: incompleteSection.id, sx: { py: 0, px: 1 } },
                                React.createElement(Typography, { sx: { color: (theme) => theme.palette.error.main, mr: 1 } }, "\u2022"),
                                React.createElement(Typography, { variant: 'subtitle2' }, sectionDetails === null || sectionDetails === void 0 ? void 0 : sectionDetails.columns.title)));
                        }))))),
                !allowUpdateFull && (React.createElement(React.Fragment, null,
                    React.createElement(Divider, null),
                    React.createElement(Alert, { severity: 'error', icon: React.createElement(LockOutlinedIcon, null) }, t('panels.accounts.notAllowedToViewAccountDetails')))))),
        allowUpdateFull && !props.hideViewProfile && (React.createElement(ModalSlideoutActions, null,
            React.createElement(Box, { sx: { width: '100%', p: 2, background: (theme) => theme.palette.background.paper } },
                React.createElement(Button, { variant: 'contained', size: 'large', color: 'primary', onClick: () => props.onViewProfile(), fullWidth: true, "data-test": 'view-full-profile-button' }, t('panels.accounts.viewProfile')))))));
};
export const OktaStatusAlert = ({ accountId, oktaStatus, reloadOktaStatus }) => {
    const { t } = useContactManagementTranslation();
    const userState = useUserState();
    const allowUnlockAccount = requireAnySystemPermission(userState, 'allow-unlock-okta-account');
    const hasResetUserPasswordPermission = requireAnySystemPermission(userState, 'allow-reset-okta-password-email');
    const unlockAccount = useTypedPlatformFunction(unlockAccountDef);
    const resetUserPassword = useTypedPlatformFunction(resetUserPasswordDef);
    if (oktaStatus === oktaStatusType.PENDING_USER_ACTION || oktaStatus === oktaStatusType.RECOVERY) {
        return React.createElement(Alert, { severity: 'warning' }, t('panels.accounts.pendingEmailVerification'));
    }
    else if (oktaStatus === oktaStatusType.PASSWORD_EXPIRED || oktaStatus === oktaStatusType.PASSWORD_RESET) {
        return (React.createElement(Alert, { severity: oktaStatus === oktaStatusType.PASSWORD_EXPIRED ? 'error' : 'warning', action: hasResetUserPasswordPermission && (React.createElement(Button, { color: 'inherit', size: 'small', onClick: () => {
                    resetUserPassword({ accountId: accountId })
                        .then(() => {
                        showAlertSnackbar({
                            message: t('panels.accounts.passwordIsReset'),
                            type: 'success',
                            autoHideDuration: 2000,
                        });
                        reloadOktaStatus();
                    })
                        .catch(handleErrorDialog);
                } }, t('panels.accounts.resend'))) }, oktaStatus === oktaStatusType.PASSWORD_EXPIRED
            ? t('panels.accounts.passwordIsExpired')
            : t('panels.accounts.pendingEmailVerification')));
    }
    else if (oktaStatus === oktaStatusType.LOCKED_OUT) {
        return (React.createElement(Alert, { severity: 'error', action: allowUnlockAccount && (React.createElement(Button, { color: 'inherit', size: 'small', onClick: () => {
                    unlockAccount({ accountId: accountId })
                        .then(() => {
                        showAlertSnackbar({
                            message: t('panels.accounts.accountIsUnlocked'),
                            type: 'success',
                            autoHideDuration: 2000,
                        });
                        reloadOktaStatus();
                    })
                        .catch(handleErrorDialog);
                } }, t('panels.accounts.unlock'))) }, t('panels.accounts.accountIsLocked')));
    }
    else {
        return React.createElement(PageError, { error: t('panels.accounts.unexpectedOktaStatus') });
    }
};
